import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import SectionScrollIndicator from '../../components/SectionScrollIndicator';
import Icon from '../../components/AppIcon';

const TermsConditions = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isSticky, setIsSticky] = useState(false);
  const tocRef = useRef(null);
  const activeTocItemRef = useRef(null);
  const isScrollingTocRef = useRef(false);

  useEffect(() => {
    document.title = 'Terms & Conditions - Dishant Madariya | Full Stack Developer';
    window.scrollTo(0, 0);

    // Handle sticky TOC
    const handleScroll = () => {
      setIsSticky(window.scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for detecting active sections
  useEffect(() => {
    const sections = document.querySelectorAll('[data-section]');
    if (sections.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px', // Trigger when section is in upper portion of viewport
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };

    const observerCallback = (entries) => {
      // Find the entry with the highest intersection ratio that's intersecting
      let maxRatio = 0;
      let activeEntry = null;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          activeEntry = entry;
        }
      });

      // If no intersecting entry, find the one closest to the top
      if (!activeEntry) {
        entries.forEach((entry) => {
          const rect = entry.boundingClientRect;
          if (rect.top >= 0 && rect.top < window.innerHeight * 0.3) {
            activeEntry = entry;
          }
        });
      }

      if (activeEntry && activeEntry.isIntersecting) {
        const sectionId = activeEntry.target.getAttribute('id');
        if (sectionId) {
          setActiveSection((prev) => {
            // Only update if different to prevent unnecessary re-renders
            return prev !== sectionId ? sectionId : prev;
          });
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, []); // Empty dependency array - only run once on mount

  // Auto-scroll TOC sidebar to keep active item visible
  useEffect(() => {
    if (!activeSection || isScrollingTocRef.current) return;

    const tocContainer = tocRef.current;
    const activeTocItem = activeTocItemRef.current;

    if (tocContainer && activeTocItem) {
      const containerRect = tocContainer.getBoundingClientRect();
      const itemRect = activeTocItem.getBoundingClientRect();

      // Check if item is outside viewport
      const isAboveViewport = itemRect.top < containerRect.top;
      const isBelowViewport = itemRect.bottom > containerRect.bottom;

      if (isAboveViewport || isBelowViewport) {
        isScrollingTocRef.current = true;

        // Calculate scroll position to center the active item
        const containerScrollTop = tocContainer.scrollTop;
        const itemOffsetTop = activeTocItem.offsetTop;
        const containerHeight = tocContainer.clientHeight;
        const itemHeight = activeTocItem.offsetHeight;

        // Center the item in the container
        const targetScrollTop = itemOffsetTop - (containerHeight / 2) + (itemHeight / 2);

        tocContainer.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });

        // Reset flag after scroll completes
        setTimeout(() => {
          isScrollingTocRef.current = false;
        }, 500);
      }
    }
  }, [activeSection]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Temporarily disable auto-scroll to prevent conflicts
      isScrollingTocRef.current = true;

      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Re-enable auto-scroll after a delay
      setTimeout(() => {
        isScrollingTocRef.current = false;
      }, 1000);
    }
  };

  const tableOfContents = [
    { id: 'introduction', label: 'Introduction & Services', number: 1 },
    { id: 'engagement', label: 'Project Engagement Process', number: 2 },
    { id: 'payment', label: 'Payment Terms', number: 3 },
    { id: 'refund', label: 'Refund Policy', number: 4 },
    { id: 'delivery', label: 'Project Delivery & Timelines', number: 5 },
    { id: 'client-responsibilities', label: 'Client Responsibilities', number: 6 },
    { id: 'intellectual-property', label: 'Intellectual Property Rights', number: 7 },
    { id: 'communication', label: 'Communication & Availability', number: 8 },
    { id: 'scope', label: 'Project Scope Management', number: 9 },
    { id: 'technical-standards', label: 'Technical Standards & Quality', number: 10 },
    { id: 'feedback', label: 'Feedback, Revisions & Approvals', number: 11 },
    { id: 'deployment', label: 'Deployment & Launch', number: 12 },
    { id: 'support', label: 'Post-Launch Support & Warranty', number: 13 },
    { id: 'confidentiality', label: 'Confidentiality', number: 14 },
    { id: 'warranties', label: 'Warranties & Limitations', number: 15 },
    { id: 'cancellation', label: 'Project Cancellation & Termination', number: 16 },
    { id: 'hosting', label: 'Hosting & Third-Party Services', number: 17 },
    { id: 'legal', label: 'Legal Compliance & Dispute Resolution', number: 18 },
    { id: 'general', label: 'General Provisions', number: 19 },
    { id: 'contact', label: 'Contact Information', number: 20 },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms & Conditions - Dishant Madariya | Full Stack Developer</title>
        <meta name="description" content="Terms and Conditions for professional services provided by Dishant Madariya. Read our policies on payments, refunds, project delivery, and more." />
      </Helmet>

      <SectionScrollIndicator />

      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
              Terms & Conditions
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed mb-4">
              Last Updated: December 8, 2025
            </p>
            <p className="text-sm md:text-base text-text-secondary max-w-3xl mx-auto">
              Welcome to Dishant Madariya's professional services. This document outlines the terms, conditions, policies, and working agreements for all services provided. By engaging my services, you agree to all terms outlined below.
            </p>
          </div>

          {/* Two-Column Layout */}
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Left Column - Table of Contents (Sticky on Desktop) */}
            <div className="lg:col-span-1">
              <div className={`lg:sticky ${isSticky ? 'lg:top-24' : 'lg:top-20'} transition-all duration-300`}>
                <div className="bg-surface rounded-lg border border-border p-6 shadow-md">
                  <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                    <Icon name="List" size={20} className="mr-2 text-primary" />
                    Table of Contents
                  </h2>
                  <nav
                    ref={tocRef}
                    className="toc-scrollbar space-y-2 max-h-[calc(100vh-12rem)] overflow-y-auto overscroll-contain pr-2"
                    style={{ scrollBehavior: 'smooth' }}
                  >
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        ref={activeSection === item.id ? activeTocItemRef : null}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 ${activeSection === item.id
                            ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary'
                            : 'text-text-secondary hover:bg-muted hover:text-text-primary'
                          }`}
                      >
                        <span className="font-medium">{item.number}.</span> {item.label}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Right Column - Main Content */}
            <div className="lg:col-span-3 space-y-8 text-left break-words whitespace-normal min-w-0">
              {/* Introduction & Services */}
              <section id="introduction" data-section="introduction" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">1</span>
                    Introduction & Services
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">1.1 About These Terms</h3>
                      <p className="text-text-secondary leading-relaxed">
                        These Terms and Conditions govern the provision of web development, software development, and related IT services offered by Dishant Madariya. By engaging my services, you acknowledge that you have read, understood, and agree to be bound by these terms.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">1.2 Services Offered</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">I provide the following professional services:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Full-stack web application development (React, Node.js)</li>
                        <li>Custom software development and solutions</li>
                        <li>API development and integration</li>
                        <li>Cloud solutions and deployment</li>
                        <li>Website maintenance and support</li>
                        <li>Technical consulting and advisory services</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Project Engagement Process */}
              <section id="engagement" data-section="engagement" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">2</span>
                    Project Engagement Process
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">2.1 Initial Consultation</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Free initial consultation (up to 30 minutes) to discuss project requirements</li>
                        <li>Detailed project scope and timeline will be provided after consultation</li>
                        <li>No obligation to proceed after consultation</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">2.2 Project Proposal</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>A formal proposal including scope, deliverables, timeline, and cost will be provided</li>
                        <li>Proposal valid for 30 days from issue date</li>
                        <li>Work begins only after written acceptance of the proposal</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">2.3 Project Agreement</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>A project agreement or contract will be signed before work commences</li>
                        <li>Agreement includes detailed scope, milestones, deliverables, and payment terms</li>
                        <li>Changes to scope require a written change request and may affect timeline and cost</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">2.4 Project Lifecycle Phases</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Phase 1 - Discovery & Planning:</strong> Requirements gathering, technical assessment, contract signing</li>
                        <li><strong>Phase 2 - Development:</strong> Iterative development with regular updates and milestone demos</li>
                        <li><strong>Phase 3 - Testing & Refinement:</strong> QA testing, client acceptance testing, bug fixes</li>
                        <li><strong>Phase 4 - Delivery & Handover:</strong> Final deliverable transfer, documentation, training</li>
                        <li><strong>Phase 5 - Support Period:</strong> 30-day warranty period with bug fixes and support</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Payment Terms */}
              <section id="payment" data-section="payment" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">3</span>
                    Payment Terms
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.1 Project-Based Pricing Structure</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">Payment terms are structured based on project size:</p>
                      <div className="space-y-4">
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Small Projects (Under ₹80,000 / $1,000)</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>50% upfront deposit</li>
                            <li>50% upon project completion and delivery</li>
                          </ul>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Medium Projects (₹80,000 - ₹4,00,000 / $1,000 - $5,000)</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>40% upfront deposit</li>
                            <li>30% at project midpoint milestone</li>
                            <li>30% upon completion and delivery</li>
                          </ul>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Large Projects (Over ₹4,00,000 / $5,000)</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>30% upfront deposit</li>
                            <li>Milestone-based payments (30-40% intervals)</li>
                            <li>Final payment upon completion</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.2 Hourly & Retainer Arrangements</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Hourly rate will be specified in the project agreement</li>
                        <li>Minimum billable increment: 30 minutes</li>
                        <li><strong>Retainer Clients:</strong> Monthly invoicing with payment due within 7 days of invoice</li>
                        <li>Detailed time tracking provided for all hourly work</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.3 Payment Methods Accepted</h3>
                      <div className="grid md:grid-cols-2 gap-4 mt-3">
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Domestic (India)</h4>
                          <p className="text-sm text-text-secondary">UPI, NEFT, RTGS, IMPS, Bank Transfer</p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">International</h4>
                          <p className="text-sm text-text-secondary">Wire Transfer, PayPal, Stripe, Wise (TransferWise)</p>
                        </div>
                      </div>
                      <p className="text-sm text-text-secondary mt-3 ml-4">Cryptocurrency: Bitcoin, Ethereum (if mutually agreed)</p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.4 Invoice Terms</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Invoices issued upon milestone completion or as per agreement</li>
                        <li>Payment due within 7 days of invoice date unless otherwise specified</li>
                        <li>GST (Goods and Services Tax) applicable as per Indian tax laws</li>
                        <li>Tax receipts and invoices provided for all transactions</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.5 Late Payment Policy</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Payments overdue by 15 days may incur a 5% late fee</li>
                        <li>Work may be paused if payment is more than 15 days overdue</li>
                        <li>Access to deliverables, source code, or hosted projects may be withheld until payment is received</li>
                        <li>Repeated late payments may result in project termination</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">3.6 Currency & Exchange Rates</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Prices quoted in INR (₹) or USD ($) as agreed</li>
                        <li>International payments: Exchange rate locked at proposal acceptance</li>
                        <li>Currency conversion fees are client's responsibility</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Refund Policy */}
              <section id="refund" data-section="refund" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">4</span>
                    Refund Policy
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.1 General Refund Philosophy</h3>
                      <p className="text-text-secondary leading-relaxed">
                        I strive for complete client satisfaction in every project. However, due to the nature of custom software development, refunds are limited as outlined below.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.2 Non-Refundable Payments</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">The following payments are <strong>non-refundable</strong> under any circumstances:</p>

                      <div className="space-y-4">
                        <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                          <h4 className="font-semibold text-text-primary mb-2">4.2.1 Upfront Deposits</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Initial deposits and upfront payments are <strong>non-refundable</strong></li>
                            <li>These payments secure your project slot in my schedule</li>
                            <li>Deposits compensate for opportunity cost and initial planning work</li>
                            <li>Applies even if you cancel before substantial work begins</li>
                          </ul>
                        </div>

                        <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                          <h4 className="font-semibold text-text-primary mb-2">4.2.2 Completed Work & Milestones</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Payments for completed milestones are non-refundable</li>
                            <li>Once work is delivered and accepted (explicitly or after review period), payment is final</li>
                            <li>You retain ownership and access to all paid work</li>
                          </ul>
                        </div>

                        <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                          <h4 className="font-semibold text-text-primary mb-2">4.2.3 Hourly & Time-Based Work</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Hours already worked are non-refundable</li>
                            <li>You pay only for actual time invested with detailed time logs provided</li>
                            <li>Unused retainer hours may be refundable (as per specific retainer agreement)</li>
                          </ul>
                        </div>

                        <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                          <h4 className="font-semibold text-text-primary mb-2">4.2.4 Third-Party Expenses</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>No refunds for third-party costs incurred on your behalf</li>
                            <li>Includes: hosting setup, domain registration, premium plugins, stock assets, API credits</li>
                            <li>These are pass-through expenses billed at cost</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.3 Eligible Refund Scenarios</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">Refunds may be issued in the following limited circumstances:</p>

                      <div className="space-y-4">
                        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                          <h4 className="font-semibold text-text-primary mb-2">4.3.1 Service Provider Failure</h4>
                          <p className="text-sm text-text-secondary ml-4">If I fail to deliver agreed services without valid reason:</p>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-8 text-sm mt-2">
                            <li>Full refund of payments for undelivered work only</li>
                            <li>Work completed and delivered to date remains yours</li>
                            <li>Must be documented failure to meet contractual obligations</li>
                          </ul>
                        </div>

                        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                          <h4 className="font-semibold text-text-primary mb-2">4.3.2 Project Not Commenced</h4>
                          <p className="text-sm text-text-secondary ml-4">If your project has not started and absolutely no work has been performed:</p>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-8 text-sm mt-2">
                            <li>Refund of deposit minus 20% administrative/opportunity cost fee</li>
                            <li>Must be requested in writing within 7 days of deposit</li>
                            <li>Subject to verification that no work has begun</li>
                          </ul>
                        </div>

                        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                          <h4 className="font-semibold text-text-primary mb-2">4.3.3 Mutual Agreement</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Refunds may be negotiated on a case-by-case basis</li>
                            <li>Both parties must agree in writing to refund terms</li>
                            <li>Typically involves partial refund with work completed transferred</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.4 Non-Refundable Situations</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">No refunds will be issued in these situations:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Change of Mind:</strong> No refunds if you change your mind after work has commenced</li>
                        <li><strong>Unreasonable or Changed Expectations:</strong> No refunds if your expectations exceed the agreed project scope</li>
                        <li><strong>Client-Caused Delays or Lack of Response:</strong> No refunds for projects delayed due to lack of client feedback or materials</li>
                        <li><strong>Dissatisfaction Without Cause:</strong> No refunds for subjective dissatisfaction when work meets agreed specifications</li>
                        <li><strong>Technical Limitations:</strong> No refunds due to technical limitations disclosed during project</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.5 Refund Request Process</h3>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border">
                        <h4 className="font-semibold text-text-primary mb-2">4.5.1 How to Request a Refund</h4>
                        <ol className="list-decimal list-inside space-y-2 text-text-secondary ml-4 text-sm">
                          <li>Send a detailed written request via email to contact.dishantpatel@gmail.com</li>
                          <li>Include: Project name, agreement reference, payment details, specific reason for refund request</li>
                          <li>Provide supporting documentation or evidence if applicable</li>
                          <li>Allow 5-7 business days for review and response</li>
                        </ol>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border mt-4">
                        <h4 className="font-semibold text-text-primary mb-2">4.5.2 Review Process</h4>
                        <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4 text-sm">
                          <li>Each refund request is reviewed individually and objectively</li>
                          <li>Response provided within 7 business days of request</li>
                          <li>Additional information may be requested during review</li>
                          <li>Decision is final and binding</li>
                        </ul>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border mt-4">
                        <h4 className="font-semibold text-text-primary mb-2">4.5.3 Refund Processing Timeline</h4>
                        <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4 text-sm">
                          <li>Approved refunds are processed within 14 business days of approval</li>
                          <li>Refunds issued via original payment method when possible</li>
                          <li>Payment processor fees (typically 2-5%) may be deducted from refund amount</li>
                          <li>International refunds may take longer due to banking processes</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.6 Partial Refunds</h3>
                      <p className="text-text-secondary leading-relaxed">
                        In certain situations, partial refunds may be offered at my discretion:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4 mt-2">
                        <li>Significant project delays caused by me without valid reason</li>
                        <li>Deliverables partially meeting agreed specifications</li>
                        <li>Mutual agreement to terminate project mid-stream</li>
                        <li>Goodwill gestures on a case-by-case basis</li>
                      </ul>
                      <p className="text-text-secondary leading-relaxed mt-3">
                        Partial refund amount determined by: work completed, time invested, resources used, and circumstances of the situation.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.7 Chargeback & Payment Dispute Policy</h3>
                      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                        <h4 className="font-semibold text-text-primary mb-2">4.7.1 Dispute Resolution First</h4>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>Payment disputes or dissatisfaction should be raised directly with me first</li>
                          <li>Good faith effort will be made to resolve all disputes amicably</li>
                          <li>Most issues can be resolved through direct communication</li>
                        </ul>
                      </div>
                      <div className="bg-error/10 border border-error/20 rounded-lg p-4 mt-4">
                        <h4 className="font-semibold text-text-primary mb-2">4.7.2 Improper Chargeback Consequences</h4>
                        <p className="text-sm text-text-secondary mb-2 ml-4">Initiating a chargeback or payment reversal without first attempting to resolve the issue with me may result in:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-8 text-sm">
                          <li>Immediate revocation of all licenses and access to deliverables</li>
                          <li>Legal action to recover funds, costs, and damages</li>
                          <li>Termination of all ongoing and future services</li>
                          <li>Reporting to credit agencies and fraud prevention services (if applicable)</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.8 Quality Guarantee (Separate from Refunds)</h3>
                      <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                        <h4 className="font-semibold text-text-primary mb-2">4.8.1 30-Day Bug Fix Warranty</h4>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>Free fixes for defects and bugs in original deliverables within 30 days of delivery</li>
                          <li>Warranty does not entitle you to a refund but ensures quality work</li>
                          <li>Covers only original deliverables as specified in agreement</li>
                          <li>Does not cover issues caused by client modifications or third-party services</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">4.9 Force Majeure</h3>
                      <p className="text-text-secondary leading-relaxed">
                        No refunds are required if project performance is prevented or delayed by circumstances beyond reasonable control, including but not limited to: natural disasters, pandemics, war, government actions, internet/power outages, or other acts of God. Work will resume when circumstances permit.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Project Delivery & Timelines */}
              <section id="delivery" data-section="delivery" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">5</span>
                    Project Delivery & Timelines
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.1 Timeline Commitments</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Estimated timelines are provided in good faith based on agreed scope</li>
                        <li>Timelines account for my workload, project complexity, and reasonable assumptions</li>
                        <li>Delays caused by client feedback delays, scope changes, or late materials extend timelines proportionally</li>
                        <li>I will communicate proactively about any potential delays or obstacles</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.2 Factors Affecting Timeline</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Client feedback and approval turnaround time</li>
                        <li>Availability of required materials, content, access, or information</li>
                        <li>Scope changes or additional feature requests</li>
                        <li>Third-party service delays or issues</li>
                        <li>Technical challenges or unforeseen complexities</li>
                        <li>Force majeure events</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.3 Client-Caused Delays</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Lack of timely feedback (beyond 48 business hours) extends timeline by equivalent delay</li>
                        <li>Projects paused due to awaiting client input for 30+ days may incur reactivation fees</li>
                        <li>Projects inactive for 60+ days may be considered abandoned and closed</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.4 Rush Projects & Expedited Delivery</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Expedited delivery available for additional 25-50% fee (based on urgency)</li>
                        <li>Must be agreed upon before project commencement</li>
                        <li>Subject to my current workload and capacity</li>
                        <li>Does not compromise quality standards</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.5 Delivery Method</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Deliverables provided via agreed method: GitHub/GitLab repository, cloud storage (Google Drive, Dropbox), direct deployment to hosting, or other mutually agreed method</li>
                        <li>Source code ownership transfers upon final payment (unless otherwise specified)</li>
                        <li>All project files, assets, and documentation included</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">5.6 Acceptance Testing</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>All deliverables undergo thorough testing before delivery to client</li>
                        <li>Client is responsible for final acceptance testing within agreed timeframe (typically 5-7 days)</li>
                        <li>Issues discovered during acceptance testing will be addressed promptly</li>
                        <li>Acceptance occurs explicitly (written approval) or implicitly (after review period with no issues raised)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Client Responsibilities */}
              <section id="client-responsibilities" data-section="client-responsibilities" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">6</span>
                    Client Responsibilities
                  </h2>

                  <div className="space-y-6 ps-4">
                    <p className="text-text-secondary leading-relaxed">
                      To ensure successful project completion, you agree to:
                    </p>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">6.1 Timely Participation</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Provide timely feedback and approvals within 48 business hours</li>
                        <li>Attend scheduled meetings, calls, and milestone reviews</li>
                        <li>Respond to queries and information requests promptly</li>
                        <li>Make decisions or obtain necessary approvals without undue delay</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">6.2 Required Materials & Access</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Supply all necessary materials, content, images, videos, and assets</li>
                        <li>Provide access to required systems, accounts, hosting, domains, and APIs</li>
                        <li>Ensure all provided content is original, legally owned, or properly licensed</li>
                        <li>Provide brand guidelines, design preferences, and style requirements</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">6.3 Payment Obligations</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Make payments according to the agreed schedule and terms</li>
                        <li>Ensure payment methods are valid and have sufficient funds</li>
                        <li>Cover any payment processing or currency conversion fees</li>
                        <li>Provide necessary invoicing information and tax details</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">6.4 Decision-Making Authority</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Ensure you have authority to approve work and make project decisions</li>
                        <li>Provide clear approval process if multiple stakeholders involved</li>
                        <li>Communicate any organizational or approval process requirements upfront</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">6.5 Honest & Complete Information</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Provide accurate, complete project requirements and objectives</li>
                        <li>Disclose any technical constraints, limitations, or special requirements</li>
                        <li>Communicate changes in business needs or priorities promptly</li>
                        <li>Be transparent about budget, timeline, and resource constraints</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Intellectual Property Rights */}
              <section id="intellectual-property" data-section="intellectual-property" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">7</span>
                    Intellectual Property Rights
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.1 Ownership Upon Full Payment</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Upon receipt of full and final payment, you own all custom code and deliverables created specifically for your project</li>
                        <li>Ownership transfer includes: custom application code, designs specific to your project, and project-specific content</li>
                        <li>Source code, documentation, and assets are transferred to you</li>
                        <li>You may use, modify, distribute, and commercialize the deliverables</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.2 Rights Retained by Developer</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">I retain ownership and rights to:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Reusable Components:</strong> Code libraries, frameworks, templates, and utilities developed by me prior to or independent of your project</li>
                        <li><strong>Development Tools:</strong> Build scripts, deployment configurations, development methodologies</li>
                        <li><strong>General Knowledge:</strong> Techniques, methodologies, and general programming knowledge</li>
                        <li><strong>Portfolio Rights:</strong> Right to showcase the project in my portfolio, case studies, and marketing materials (unless confidentiality/NDA agreed)</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.3 License to Pre-Existing Materials</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>For pre-existing code, components, or materials incorporated into your project, you receive a perpetual, non-exclusive license to use these as part of the delivered project</li>
                        <li>You cannot extract and reuse my pre-existing components in other projects without separate agreement</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.4 Third-Party Components & Open Source</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Third-party libraries, frameworks, and open-source components remain under their respective licenses (MIT, GPL, Apache, etc.)</li>
                        <li>You are responsible for compliance with third-party license terms</li>
                        <li>I will inform you of any third-party components with restrictive licenses</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.5 Ownership Before Final Payment</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Until full payment is received, I retain all ownership rights to the work product</li>
                        <li>You receive a conditional license to review and test deliverables</li>
                        <li>Full ownership and source code transfer occurs only upon complete payment</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">7.6 Client-Provided Materials</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>You retain ownership of all materials, content, and assets you provide</li>
                        <li>You grant me a license to use these materials solely for your project</li>
                        <li>You represent and warrant that you have rights to all materials provided</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Communication & Availability */}
              <section id="communication" data-section="communication" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">8</span>
                    Communication & Availability
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">8.1 Primary Communication Channels</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Email:</strong> contact.dishantpatel@gmail.com (for formal communication, approvals, and documentation)</li>
                        <li><strong>Project Management Tools:</strong> Trello, Asana, Jira, or as mutually agreed</li>
                        <li><strong>Video Calls:</strong> Google Meet, Zoom (for kickoff meetings and milestone reviews)</li>
                        <li><strong>Messaging:</strong> WhatsApp, Slack, or Telegram (for quick queries, if agreed upon)</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">8.2 Response Time Expectations</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>My Response Time:</strong> Within 24 business hours during working hours for all non-urgent queries</li>
                        <li><strong>Client Response Expected:</strong> Within 48 business hours to maintain project momentum</li>
                        <li><strong>Urgent Matters:</strong> Critical production issues addressed as quickly as possible; mark as "URGENT" in subject line</li>
                        <li><strong>Weekends & Holidays:</strong> Non-urgent communication may be answered on next business day</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">8.3 Working Hours & Availability</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Standard Working Hours:</strong> 7:00 PM to 8:00 AM IST (Indian Standard Time), 7 days a week</li>
                        <li><strong>Time Zone:</strong> All deadlines and schedules in IST unless otherwise specified</li>
                        <li><strong>Emergency Support:</strong> Available only for critical production issues affecting live applications (additional fees may apply)</li>
                        <li><strong>Planned Time Off:</strong> Holidays, vacations, and planned absences communicated at least 7 days in advance</li>
                        <li><strong>Indian Public Holidays:</strong> Major Indian holidays will be observed; communicated in advance</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">8.4 Meeting & Call Protocol</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Meetings scheduled at mutually convenient times with at least 24 hours notice</li>
                        <li>Agenda provided before scheduled calls when possible</li>
                        <li>Video calls preferred for kickoffs, milestone reviews, and complex discussions</li>
                        <li>Meeting notes and action items documented and shared</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Project Scope Management */}
              <section id="scope" data-section="scope" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold min-w-[2.5rem]">9</span>
                    Project Scope Management
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">9.1 Scope Definition</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Detailed project scope documented in the project agreement/proposal</li>
                        <li>Features, functionalities, and deliverables clearly listed and described</li>
                        <li>Out-of-scope items explicitly identified to avoid confusion</li>
                        <li>Technical specifications and requirements documented</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">9.2 Scope Changes & Change Requests</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>All changes to agreed scope require written approval via change request</li>
                        <li>Change requests reviewed for: technical feasibility, impact on timeline, impact on cost</li>
                        <li>Change order issued detailing additional cost and timeline before work proceeds</li>
                        <li>Client approval of change order required before implementing changes</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">9.3 Minor vs. Major Changes</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Minor Changes:</strong> Small tweaks within the spirit of original scope may be accommodated without additional charge</li>
                        <li><strong>Major Changes:</strong> New features, significant modifications, or additional functionality require change order</li>
                        <li>Distinction determined based on time impact, complexity, and deviation from original agreement</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">9.4 Scope Creep Prevention</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Regular scope reviews at milestone checkpoints</li>
                        <li>Clear communication about what is and isn't included</li>
                        <li>Additional requests documented and addressed through change request process</li>
                        <li>Proactive identification of potential scope creep</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">9.5 Feature Prioritization</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>If scope proves too ambitious for timeline/budget, features may be prioritized</li>
                        <li>Client input required for prioritization decisions</li>
                        <li>MVP (Minimum Viable Product) approach may be suggested for phased delivery</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Technical Standards & Quality */}
              <section id="technical-standards" data-section="technical-standards" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">10</span>
                    Technical Standards & Quality
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">10.1 Code Quality Standards</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Clean, readable, and maintainable code following industry best practices</li>
                        <li>Proper code structure, organization, and architectural patterns</li>
                        <li>Meaningful variable names, functions, and code comments</li>
                        <li>DRY (Don't Repeat Yourself) and SOLID principles followed</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">10.2 Version Control & Documentation</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Git used for version control with clear, descriptive commit messages</li>
                        <li>Code documentation provided for complex logic and functions</li>
                        <li>README files with setup instructions, dependencies, and usage guidelines</li>
                        <li>API documentation for backend services (if applicable)</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">10.3 Security Best Practices</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Secure coding practices to prevent common vulnerabilities (SQL injection, XSS, CSRF, etc.)</li>
                        <li>Sensitive data protection (encryption, secure storage)</li>
                        <li>Authentication and authorization implemented properly</li>
                        <li>Security updates and patches applied to dependencies</li>
                        <li>HTTPS/SSL enforced for production applications</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">10.4 Performance Optimization</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Code and database query optimization</li>
                        <li>Asset optimization (image compression, minification, lazy loading)</li>
                        <li>Caching strategies implemented where appropriate</li>
                        <li>Fast loading times and responsive user experience</li>
                        <li>Scalability considerations for future growth</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">10.5 Cross-Browser & Device Compatibility</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Testing across major modern browsers (Chrome, Firefox, Safari, Edge)</li>
                        <li>Responsive design for mobile, tablet, and desktop devices</li>
                        <li>Graceful degradation for older browsers (if specified in scope)</li>
                        <li>Progressive enhancement approach</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">10.6 Testing & Quality Assurance</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Unit Testing:</strong> Critical functions and business logic tested</li>
                        <li><strong>Integration Testing:</strong> Component and API integration verified</li>
                        <li><strong>User Acceptance Testing:</strong> Functional testing against requirements</li>
                        <li><strong>Browser/Device Testing:</strong> Cross-platform compatibility verified</li>
                        <li><strong>Performance Testing:</strong> Load times and responsiveness checked</li>
                        <li>Bug tracking and systematic issue resolution</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">10.7 Technology Stack & Standards</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Modern, industry-standard, and well-supported technologies used</li>
                        <li>Specific technologies agreed upon in proposal (React, Node.js, etc.)</li>
                        <li>Client approval sought for any major technology decisions</li>
                        <li>Preference for stable, mature technologies over cutting-edge but unstable options</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Feedback, Revisions & Approvals */}
              <section id="feedback" data-section="feedback" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">11</span>
                    Feedback, Revisions & Approvals
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">11.1 Review & Feedback Process</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Structured review periods at each milestone and project phase</li>
                        <li>Feedback requested at specific checkpoints, not continuously throughout development</li>
                        <li>Consolidated feedback preferred (one comprehensive list rather than piecemeal comments)</li>
                        <li>Specific, actionable, and detailed feedback required for efficient implementation</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">11.2 Feedback Timing & Turnaround</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Client given 5-7 business days for review and feedback at each milestone</li>
                        <li>Feedback must be provided in writing via email or project management tool</li>
                        <li>Extended review periods may delay project timeline proportionally</li>
                        <li>Failure to provide feedback within agreed timeframe constitutes implicit approval</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">11.3 Approval Process</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Explicit written approval required for each project phase before proceeding</li>
                        <li>Approval indicates acceptance of work completed to that point</li>
                        <li>Changes requested after approval of a phase may incur additional costs</li>
                        <li>Final approval required before project is considered complete</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">11.4 Revision Rounds & Limits</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Number of revision rounds specified in project agreement (typically 2-3 rounds)</li>
                        <li>Revisions must be within the original agreed scope</li>
                        <li>Each revision round allows changes across entire deliverable</li>
                        <li>Revisions beyond agreed rounds quoted separately</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">11.5 What Constitutes a Revision</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Changes to design, layout, functionality, or content within original scope</li>
                        <li>Bug fixes and issue corrections (unlimited during development and warranty period)</li>
                        <li>Performance improvements and optimizations (within reason)</li>
                        <li>Clarifications and adjustments based on misunderstandings</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">11.6 Out-of-Scope Revisions</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">Additional charges apply for:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>New features or functionality not in original scope</li>
                        <li>Complete redesigns or major directional changes</li>
                        <li>Changes based on new requirements or business needs</li>
                        <li>Revisions beyond agreed number of rounds</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Deployment & Launch */}
              <section id="deployment" data-section="deployment" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">12</span>
                    Deployment & Launch
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">12.1 Deployment Assistance Included</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Deployment to agreed hosting environment (client-provided or recommended)</li>
                        <li>Initial server and environment configuration</li>
                        <li>SSL certificate installation and HTTPS setup</li>
                        <li>Database setup and migration</li>
                        <li>Basic performance and functionality verification after deployment</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">12.2 Client Hosting Responsibilities</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">You must provide or arrange:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Hosting account with appropriate specifications for the application</li>
                        <li>Domain name registration and access to DNS settings</li>
                        <li>Access credentials to hosting, cPanel, SSH, database, etc.</li>
                        <li>Payment for hosting services (unless specifically included in project cost)</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">12.3 Pre-Launch Checklist</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">Before going live, we ensure:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>All functionality tested and working correctly</li>
                        <li>Content reviewed and approved by client</li>
                        <li>SEO basics implemented (meta tags, sitemap, robots.txt)</li>
                        <li>Analytics and tracking codes installed (if provided)</li>
                        <li>SSL certificate active and HTTPS working</li>
                        <li>Forms and integrations tested on production environment</li>
                        <li>Backup system in place</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">12.4 Go-Live Process</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Final client approval required before making site/application live</li>
                        <li>Staging-to-production migration executed during agreed timeframe (typically low-traffic hours)</li>
                        <li>DNS changes coordinated (may take 24-48 hours to propagate)</li>
                        <li>Post-launch monitoring for 24-48 hours to catch immediate issues</li>
                        <li>Critical issues during initial 48 hours resolved with priority</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">12.5 Post-Launch Immediate Support</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Monitoring and immediate bug fixes for first 48 hours after launch</li>
                        <li>Critical issues affecting functionality addressed immediately</li>
                        <li>Performance and uptime monitoring during initial period</li>
                        <li>Availability for urgent issues during launch period</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Post-Launch Support & Warranty */}
              <section id="support" data-section="support" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">13</span>
                    Post-Launch Support & Warranty
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">13.1 30-Day Bug Fix Warranty</h3>
                      <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-4">
                        <p className="font-semibold text-text-primary mb-2">Included free of charge:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>Bug fixes for defects in original deliverables within 30 days of final delivery</li>
                          <li>Issues that prevent functionality from working as specified in agreement</li>
                          <li>Compatibility issues with specified browsers/devices</li>
                          <li>Performance issues caused by implementation flaws</li>
                          <li>Email support for technical questions and clarifications</li>
                        </ul>
                      </div>
                      <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                        <p className="font-semibold text-text-primary mb-2">Not covered by warranty:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>New features or enhancements beyond original scope</li>
                          <li>Issues caused by client modifications to code or content</li>
                          <li>Third-party service failures or changes</li>
                          <li>Hosting or server-side issues outside application code</li>
                          <li>Browser updates or changes occurring after delivery</li>
                          <li>Issues caused by client's hosting environment or configuration</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">13.2 Warranty Claim Process</h3>
                      <ol className="list-decimal list-inside space-y-2 text-text-secondary ml-4">
                        <li>Report issue via email with detailed description and screenshots/videos</li>
                        <li>Issue reviewed and severity assessed within 24 business hours</li>
                        <li>Timeline for fix provided based on severity and complexity</li>
                        <li>Fix implemented, tested, and deployed</li>
                        <li>Client verification of fix</li>
                      </ol>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">13.3 Extended Support Options</h3>
                      <div className="space-y-4">
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Monthly Maintenance Packages Available:</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Priority email and phone support</li>
                            <li>Regular updates and security patches</li>
                            <li>Content updates (limited hours per month)</li>
                            <li>Performance monitoring and optimization</li>
                            <li>Backup management and security monitoring</li>
                            <li>Software and plugin updates</li>
                            <li>Custom pricing based on needs</li>
                          </ul>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Ongoing Development Retainers:</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Reserved hours per month for ongoing development</li>
                            <li>New feature development and enhancements</li>
                            <li>Priority support and rapid response times</li>
                            <li>Discounted hourly rates for retainer clients</li>
                          </ul>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Emergency Support Agreements:</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>24/7 emergency support for critical production issues</li>
                            <li>Guaranteed response times (1-4 hours based on severity)</li>
                            <li>For mission-critical applications requiring high availability</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">13.4 After Warranty Period</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Support available on hourly basis or through maintenance package</li>
                        <li>Bug fixes after warranty period may incur charges unless caused by original implementation defect</li>
                        <li>Recommendations provided for long-term maintenance approach</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Confidentiality */}
              <section id="confidentiality" data-section="confidentiality" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">14</span>
                    Confidentiality
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">14.1 Confidential Information Protection</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>All proprietary, sensitive, and confidential information shared by you will be kept strictly confidential</li>
                        <li>Confidential information includes: business plans, financial data, user data, trade secrets, proprietary processes, and any information marked as confidential</li>
                        <li>I will not disclose your confidential information to any third parties without your explicit written consent</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">14.2 Non-Disclosure Agreements (NDA)</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Mutual NDA can be signed upon request before project discussions begin</li>
                        <li>Happy to sign your standard NDA template or provide mine</li>
                        <li>NDA terms supersede these general confidentiality provisions</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">14.3 Use of Information</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Your confidential information used solely for the purpose of delivering your project</li>
                        <li>No commercial use of your information for my benefit</li>
                        <li>Information not used for training, examples, or demonstrations without anonymization and approval</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">14.4 Data Security</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Reasonable security measures implemented to protect your data during project</li>
                        <li>Secure communication channels used for sensitive information</li>
                        <li>Access restricted to information necessary for project completion</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">14.5 Post-Project Data Handling</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Your data securely stored during project and warranty period</li>
                        <li>Data can be deleted upon request after project completion</li>
                        <li>Backups maintained for reasonable period for warranty support</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">14.6 Portfolio & Marketing Use</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Unless confidentiality or NDA is in place, I may showcase completed project in portfolio, case studies, website, and marketing materials</li>
                        <li>May include: screenshots, descriptions, technologies used, and general project outcomes</li>
                        <li>Sensitive business information or data will not be disclosed</li>
                        <li>If you prefer complete confidentiality, please specify before project begins</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Warranties & Limitations */}
              <section id="warranties" data-section="warranties" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">15</span>
                    Warranties & Limitations
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">15.1 Warranty of Services</h3>
                      <p className="text-text-secondary leading-relaxed mb-3">I warrant that:</p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Services will be performed with professional skill, care, and diligence</li>
                        <li>Work will substantially conform to agreed specifications and requirements</li>
                        <li>Deliverables will be my original work or properly licensed</li>
                        <li>I have the right and authority to enter into this agreement</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">15.2 30-Day Defect Warranty</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Deliverables will function materially as specified for 30 days from delivery</li>
                        <li>Defects and bugs will be fixed free of charge during warranty period</li>
                        <li>Warranty covers only the original deliverables as specified in agreement</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">15.3 Limitations of Warranty</h3>
                      <p className="text-text-secondary leading-relaxed mb-3"><strong>This warranty does not cover:</strong></p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Issues caused by modifications made by client or third parties</li>
                        <li>Problems arising from client's hosting environment, server configuration, or infrastructure</li>
                        <li>Third-party service failures, API changes, or discontinuations</li>
                        <li>Browser updates, device changes, or OS updates after delivery</li>
                        <li>Normal wear and tear or evolving industry standards</li>
                        <li>Force majeure events or circumstances beyond reasonable control</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">15.4 Disclaimer of Other Warranties</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Services provided "as is" beyond the explicit 30-day warranty period</li>
                        <li>No warranty of uninterrupted or error-free operation</li>
                        <li>No warranty of compatibility with future technologies or updates</li>
                        <li>No warranty that software will meet all of your business objectives or generate specific results</li>
                        <li><strong>All implied warranties, including merchantability and fitness for a particular purpose, are disclaimed to the maximum extent permitted by law</strong></li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">15.5 Limitation of Liability</h3>
                      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-4">
                        <p className="font-semibold text-text-primary mb-2">Maximum Liability:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>My total liability for any claims arising from the project is limited to the total amount you paid for the project</li>
                          <li>This limit applies regardless of the form of action (contract, tort, negligence, etc.)</li>
                        </ul>
                      </div>
                      <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-4">
                        <p className="font-semibold text-text-primary mb-2">Excluded Damages:</p>
                        <p className="text-sm text-text-secondary mb-2 ml-4">I am not liable for:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-8 text-sm">
                          <li>Indirect, incidental, special, consequential, or punitive damages</li>
                          <li>Lost profits, revenue, data, or business opportunities</li>
                          <li>Cost of substitute services or downtime</li>
                          <li>Damages arising from third-party services or integrations</li>
                          <li>Damages from unauthorized access, hacking, or security breaches beyond my control</li>
                        </ul>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border">
                        <p className="font-semibold text-text-primary mb-2">Exceptions:</p>
                        <p className="text-sm text-text-secondary ml-4">Liability limitations do not apply to:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-8 text-sm">
                          <li>Gross negligence or willful misconduct on my part</li>
                          <li>Violations of applicable law that cannot be limited by contract</li>
                          <li>Death or personal injury caused by my negligence</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">15.6 Indemnification</h3>
                      <p className="text-text-secondary leading-relaxed mb-3"><strong>You agree to indemnify and hold me harmless from any claims arising from:</strong></p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Your use or misuse of the delivered project</li>
                        <li>Content, data, or materials you provide for the project</li>
                        <li>Your violation of any laws, regulations, or third-party rights</li>
                        <li>Your business operations or decisions made using the project</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">15.7 Third-Party Services & Integrations</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>No warranty or liability for third-party services, APIs, libraries, or platforms integrated into your project</li>
                        <li>Third-party services subject to their own terms, availability, and limitations</li>
                        <li>I will use reasonable efforts to select reliable third-party services but cannot guarantee their performance</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Project Cancellation & Termination */}
              <section id="cancellation" data-section="cancellation" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">16</span>
                    Project Cancellation & Termination
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">16.1 Cancellation by Client</h3>
                      <p className="text-text-secondary leading-relaxed mb-3"><strong>You may cancel the project at any time with written notice. In case of cancellation:</strong></p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li><strong>Payment for Completed Work:</strong> You must pay for all work completed up to the cancellation date based on milestones or hours worked</li>
                        <li><strong>Non-Refundable Deposits:</strong> All upfront deposits and payments already made are non-refundable (see Refund Policy)</li>
                        <li><strong>Delivery of Work:</strong> All work completed and paid for up to cancellation date will be delivered to you</li>
                        <li><strong>Outstanding Invoices:</strong> All outstanding invoices must be paid within 7 days of cancellation</li>
                        <li><strong>Intellectual Property:</strong> Ownership of paid work transfers to you; unpaid work remains my property</li>
                      </ul>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border mt-4">
                        <p className="font-semibold text-text-primary mb-2">Cancellation Timeline:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>Cancellation effective upon written notice received</li>
                          <li>Payment settlement must occur within 7 days of cancellation</li>
                          <li>Work delivery occurs upon full payment of amounts owed</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">16.2 Cancellation by Service Provider (Me)</h3>
                      <p className="text-text-secondary leading-relaxed mb-3"><strong>I reserve the right to cancel a project if:</strong></p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Terms of this agreement are materially breached by client</li>
                        <li>Payments are consistently late or not made</li>
                        <li>Client fails to provide necessary materials, access, or cooperation</li>
                        <li>Project requirements change substantially from original agreement</li>
                        <li>Client engages in abusive, threatening, or inappropriate behavior</li>
                      </ul>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border mt-4">
                        <p className="font-semibold text-text-primary mb-2">In case of cancellation by me:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>Full refund of any unearned payments</li>
                          <li>Work completed to date delivered to you</li>
                          <li>No penalty or fees charged to you (unless cancellation due to payment default)</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">16.3 Mutual Termination</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Both parties may agree to terminate project at any time</li>
                        <li>Terms negotiated in good faith including payment settlement and work delivery</li>
                        <li>Agreement documented in writing</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">16.4 Project Abandonment</h3>
                      <p className="text-text-secondary leading-relaxed mb-3"><strong>A project is considered abandoned if:</strong></p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>No client response for 60 consecutive days despite multiple follow-up attempts</li>
                        <li>Client fails to provide required materials for 60+ days</li>
                        <li>No payment activity for 60+ days on payment-pending milestones</li>
                      </ul>
                      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mt-4">
                        <p className="font-semibold text-text-primary mb-2">Upon abandonment:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>Project closed with no refunds</li>
                          <li>Client must pay all outstanding invoices to receive work completed</li>
                          <li>Restart requires new agreement and possible restart fee</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">16.5 Effect of Termination</h3>
                      <p className="text-text-secondary leading-relaxed mb-3"><strong>Upon termination of project:</strong></p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>All ongoing work stops immediately</li>
                        <li>Outstanding invoices become due within 7 days</li>
                        <li>Paid work delivered; unpaid work retained by me</li>
                        <li>Licenses granted for paid work; unpaid work licenses revoked</li>
                        <li>Confidentiality obligations continue indefinitely</li>
                        <li>No liability for consequential damages from termination</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Hosting & Third-Party Services */}
              <section id="hosting" data-section="hosting" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">17</span>
                    Hosting & Third-Party Services
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">17.1 Hosting Not Included</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Hosting services are not included in development costs unless explicitly stated</li>
                        <li>You are responsible for obtaining and paying for hosting services</li>
                        <li>I can provide recommendations for reliable hosting providers</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">17.2 Hosting Setup Assistance</h3>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border">
                        <p className="font-semibold text-text-primary mb-2">I can assist with:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>Recommending appropriate hosting solutions for your project</li>
                          <li>Initial deployment and configuration on your hosting account</li>
                          <li>SSL certificate installation</li>
                          <li>Domain DNS configuration</li>
                          <li>Basic server optimization</li>
                        </ul>
                      </div>
                      <p className="text-text-secondary text-sm mt-3 ml-4 italic">
                        Ongoing hosting management typically requires separate maintenance agreement
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">17.3 Third-Party Service Costs</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Costs for third-party services, APIs, and integrations are your responsibility unless otherwise agreed</li>
                        <li>Examples: payment gateways, email services, SMS services, map APIs, cloud storage, CDNs</li>
                        <li>Estimated costs for third-party services disclosed during planning phase</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">17.4 Third-Party Service Selection</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>I select reliable, well-established third-party services when possible</li>
                        <li>Client approval sought for any third-party services with ongoing costs</li>
                        <li>Alternative options presented when available</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">17.5 No Liability for Third-Party Issues</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>I am not liable for third-party service outages, downtime, or failures</li>
                        <li>Not responsible for third-party service pricing changes or policy changes</li>
                        <li>Not liable for data breaches or security issues at third-party service level</li>
                        <li>Third-party services subject to their own terms of service and SLAs</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">17.6 Client Accounts & Subscriptions</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Third-party accounts and subscriptions should be in client's name when possible</li>
                        <li>Ensures continued access and control after project completion</li>
                        <li>I may create accounts on your behalf with your authorization and credentials</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Legal Compliance & Dispute Resolution */}
              <section id="legal" data-section="legal" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">18</span>
                    Legal Compliance & Dispute Resolution
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">18.1 Governing Law</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>This agreement governed by the laws of India</li>
                        <li>Specifically subject to jurisdiction of Surat, Gujarat, India</li>
                        <li>Indian Contract Act, 1872 and Information Technology Act, 2000 applicable</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">18.2 Legal Compliance Responsibilities</h3>
                      <div className="space-y-4">
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <p className="font-semibold text-text-primary mb-2">My Responsibilities:</p>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Comply with applicable Indian laws in service delivery</li>
                            <li>Follow technical best practices and industry standards</li>
                            <li>Implement basic security and data protection measures</li>
                            <li>Provide GST-compliant invoicing</li>
                          </ul>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <p className="font-semibold text-text-primary mb-2">Your Responsibilities:</p>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Ensure content and use of deliverables complies with all applicable laws</li>
                            <li>Obtain necessary licenses, permissions, and rights for content provided</li>
                            <li>Comply with privacy laws (GDPR, India's Digital Personal Data Protection Act, etc.) in data handling</li>
                            <li>Ensure business operations using deliverables are legally compliant</li>
                            <li>Comply with industry-specific regulations relevant to your business</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">18.3 Accessibility Compliance</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Basic accessibility best practices followed in development</li>
                        <li>WCAG 2.1 Level AA compliance available as additional service (if specified in scope)</li>
                        <li>Full accessibility compliance is your responsibility as website/application owner</li>
                        <li>Accessibility audits and remediation available as separate service</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">18.4 Dispute Resolution Process</h3>
                      <div className="space-y-4">
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Step 1 - Direct Communication:</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Good faith attempt to resolve any disputes through direct communication</li>
                            <li>Most issues resolved through honest, professional discussion</li>
                            <li>Open dialogue encouraged throughout relationship</li>
                          </ul>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Step 2 - Formal Written Notice:</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>If direct communication fails, formal written notice of dispute sent by either party</li>
                            <li>Notice must detail specific issues and proposed resolution</li>
                            <li>15 days given for response and resolution attempt</li>
                          </ul>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Step 3 - Mediation:</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>If unresolved after written notice, mediation by mutually agreed neutral third party</li>
                            <li>Mediation costs shared equally</li>
                            <li>Good faith participation required</li>
                          </ul>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Step 4 - Arbitration:</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>If mediation fails, binding arbitration in Surat, Gujarat, India</li>
                            <li>Arbitration under Indian Arbitration and Conciliation Act, 1996</li>
                            <li>Arbitrator's decision is final and binding</li>
                            <li>Each party bears their own arbitration costs unless arbitrator decides otherwise</li>
                          </ul>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4 border border-border">
                          <h4 className="font-semibold text-text-primary mb-2">Step 5 - Legal Action:</h4>
                          <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                            <li>Only after above steps exhausted may either party pursue legal action</li>
                            <li>Legal proceedings conducted in courts of Surat, Gujarat, India</li>
                            <li>Prevailing party may be entitled to recover reasonable attorney fees</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">18.5 Jurisdiction & Venue</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Exclusive jurisdiction: Courts of Surat, Gujarat, India</li>
                        <li>Both parties consent to personal jurisdiction in Surat, Gujarat</li>
                        <li>Any legal action must be brought in Surat</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* General Provisions */}
              <section id="general" data-section="general" className="scroll-mt-24">
                <div className="bg-surface rounded-lg p-6 md:p-8 shadow-md border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">19</span>
                    General Provisions
                  </h2>

                  <div className="space-y-6 ps-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.1 Entire Agreement</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>These Terms & Conditions, together with the project proposal and signed agreement, constitute the entire agreement between parties</li>
                        <li>Supersedes all prior discussions, negotiations, and agreements</li>
                        <li>No oral modifications; all changes must be in writing and signed by both parties</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.2 Severability</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>If any provision found unenforceable or invalid by a court, that provision will be modified to make it enforceable while preserving intent</li>
                        <li>All other provisions remain in full force and effect</li>
                        <li>Invalid provisions do not invalidate the entire agreement</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.3 Waiver</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Failure to enforce any provision does not constitute waiver of that provision</li>
                        <li>Waiver of one breach does not waive future breaches</li>
                        <li>All waivers must be in writing</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.4 Assignment</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>You may not assign or transfer your rights or obligations without my written consent</li>
                        <li>I may assign or subcontract work with your knowledge, maintaining quality and confidentiality</li>
                        <li>Agreement binds and benefits both parties and permitted successors</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.5 Independent Contractor</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>I am an independent contractor, not an employee, partner, or agent</li>
                        <li>No authority to bind you or make commitments on your behalf</li>
                        <li>Responsible for my own taxes, insurance, and business expenses</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.6 Force Majeure</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Neither party liable for failure to perform due to circumstances beyond reasonable control</li>
                        <li>Includes: natural disasters, war, terrorism, pandemics, government actions, internet outages, power failures</li>
                        <li>Affected party must notify the other promptly</li>
                        <li>Performance obligations suspended during force majeure event</li>
                        <li>If event continues beyond 60 days, either party may terminate without penalty</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.7 Notices</h3>
                      <p className="text-text-secondary leading-relaxed mb-3"><strong>All formal notices must be in writing and sent to:</strong></p>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border">
                        <p className="font-semibold text-text-primary mb-2">For Client:</p>
                        <p className="text-sm text-text-secondary ml-4">Address or email provided in project agreement</p>
                        <p className="font-semibold text-text-primary mb-2 mt-4">For Service Provider:</p>
                        <ul className="list-disc list-inside space-y-1 text-text-secondary ml-4 text-sm">
                          <li>Email: contact.dishantpatel@gmail.com</li>
                          <li>Location: Surat, Gujarat, India</li>
                        </ul>
                      </div>
                      <p className="text-text-secondary text-sm mt-3 ml-4">
                        Notices deemed received: 24 hours after email sent, or 3 days after physical mail posted
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.8 Survival</h3>
                      <p className="text-text-secondary leading-relaxed mb-3"><strong>The following provisions survive termination or expiration of agreement:</strong></p>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Payment obligations</li>
                        <li>Intellectual property provisions</li>
                        <li>Confidentiality obligations</li>
                        <li>Warranties and limitations of liability</li>
                        <li>Indemnification</li>
                        <li>Dispute resolution provisions</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.9 Headings & Interpretation</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Section headings for convenience only; do not affect interpretation</li>
                        <li>"Including" means "including but not limited to"</li>
                        <li>Singular includes plural and vice versa</li>
                        <li>"Days" means business days unless specified as calendar days</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.10 Language</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>Agreement in English; English version prevails in any interpretation</li>
                        <li>Translations provided for convenience only</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.11 Amendments & Updates</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>These Terms & Conditions may be updated periodically</li>
                        <li>Current clients governed by terms accepted at project commencement</li>
                        <li>New projects subject to terms in effect at time of engagement</li>
                        <li>Material changes to terms will be communicated to active clients</li>
                        <li>Continued engagement after notification constitutes acceptance of updated terms</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-3">19.12 Relationship of Parties</h3>
                      <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
                        <li>No partnership, joint venture, or employment relationship created</li>
                        <li>Each party independent business entity</li>
                        <li>No fiduciary relationship created</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contact Information Section */}
              <section id="contact" data-section="contact" className="scroll-mt-24">
                <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-6 md:p-8 shadow-md border border-primary/20">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6 flex items-center">
                    <span className="text-primary text-3xl md:text-4xl font-bold mr-4 min-w-[2.5rem]">20</span>
                    Contact Information
                  </h2>

                  <div className="space-y-4 ml-[calc(2.5rem+1rem)]">
                    <p className="text-text-secondary leading-relaxed">
                      For questions, clarifications, or issues regarding these Terms & Conditions, please contact:
                    </p>

                    <div className="bg-surface rounded-lg p-6 border border-border">
                      <h3 className="text-xl font-semibold text-text-primary mb-4">Dishant Madariya</h3>
                      <p className="text-text-secondary mb-4 font-medium">Full Stack Developer</p>

                      <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                          <Icon name="Mail" size={20} className="text-primary mt-1" />
                          <div>
                            <p className="text-sm font-medium text-text-primary">Email</p>
                            <a href="mailto:contact.dishantpatel@gmail.com" className="text-primary hover:underline text-sm">
                              contact.dishantpatel@gmail.com
                            </a>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <Icon name="Globe" size={20} className="text-primary mt-1" />
                          <div>
                            <p className="text-sm font-medium text-text-primary">Website</p>
                            <a href="https://port-folio-three-teal.vercel.app/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">
                              https://port-folio-three-teal.vercel.app/
                            </a>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <Icon name="MapPin" size={20} className="text-primary mt-1" />
                          <div>
                            <p className="text-sm font-medium text-text-primary">Location</p>
                            <p className="text-text-secondary text-sm">Surat, Gujarat, India</p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <Icon name="Clock" size={20} className="text-primary mt-1" />
                          <div>
                            <p className="text-sm font-medium text-text-primary">Business Hours</p>
                            <p className="text-text-secondary text-sm">7:00 PM to 8:00 AM IST (Indian Standard Time)</p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <Icon name="MessageCircle" size={20} className="text-primary mt-1" />
                          <div>
                            <p className="text-sm font-medium text-text-primary">Response Time</p>
                            <p className="text-text-secondary text-sm">Within 24 business hours for all inquiries</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Acceptance Section */}
              <section className="scroll-mt-24">
                <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-6 md:p-8 shadow-md border-2 border-primary/30">
                  <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-4">Acceptance of Terms</h2>
                  <p className="text-text-secondary leading-relaxed mb-4">
                    By engaging my services, making a payment, or signing a project agreement, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions in their entirety.
                  </p>
                  <p className="text-text-secondary leading-relaxed font-medium">
                    These terms are effective as of the date of your acceptance and govern our entire professional relationship.
                  </p>
                  <div className="mt-6 pt-6 border-t border-border">
                    <p className="text-sm text-text-secondary">
                      <strong>Last Updated:</strong> December 8, 2025<br />
                      <strong>Version:</strong> 1.0
                    </p>
                    <p className="text-sm text-text-secondary mt-2 italic">
                      These Terms & Conditions constitute a legally binding agreement. Please read carefully and contact me with any questions before proceeding with any project.
                    </p>
                  </div>
                </div>
              </section>

              {/* Note: Additional sections (5-19) would follow the same pattern */}
              {/* For brevity, I'm showing the structure. You can expand each section similarly */}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsConditions;

